<template>
  <div class="card">
    <div class="bg-blue-light p-2">
      <TitleButton
          :showBtn="false"
          title="General Ledger - All Accounts"
      />

      <DateQuerySetter
          :inEnoughSpace="false"
          btnTitle="Go"
          @onClickGo="onClickGo"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label for="colFormLabel" class="col-form-label">Party: </label>
            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
              <i class="fas fa-search"></i>
            </span>
          </div>
          <AsyncSelect
            placeholder="Select Party"
            v-model="contactProfile"
            :api-service="fetchContactProfiles"
            :format-label="formatPatientLabel"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Business</label>
          <v-select
            placeholder="Select Business"
            class="w-100"
            :options="businesses"
            label="name"
            :reduce="(name) => name.id"
            v-model="businessId"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Cost Center: </label>
          <v-select placeholder="Select Cost Centre" v-model="costCenterId" :options="costCentres"
            label="name" :reduce="name => name.id" />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Project</label>
          <v-select placeholder="Select Project" v-model="projectId" :options="projects" label="name"
            :reduce="name => name.id" />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Voucher Type</label>
          <select class="form-control" v-model="voucherType">
            <option :value="null" selected>Select voucher type</option>
            <option
              v-for="voucherType in Object.keys(homeData.voucher_type)"
              :value="voucherType" :key="voucherType"
            >
              {{ homeData.voucher_type[voucherType] }}
            </option>
          </select>
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">txn Type</label>
          <select class="form-control" v-model="txnType">
            <option :value="null" selected>Select txn type</option>
            <option :value="txnType" :key="txnType" v-for="txnType in typeKeys">
              {{ homeData.txn_type[txnType] }}
            </option>
          </select>
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Created By: </label>
          <v-select placeholder="Created By" class="w-100" :options="contactList" label="name"
            :reduce="(name) => name.id" v-model="createdBy" />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Tag Register</label>
          <v-select
            placeholder="Select Tag Register"
            class="w-100"
            :options="tagRegisters"
            label="name"
            :reduce="name => name.id"
            v-model="tagRegisterId"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div v-if="ledgerList.length" class="d-flex justify-content-end px-2 pt-1">
      <button
        @click="exportTable"
        class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
      >
        Export
      </button>
    </div>

    <div v-for="item in visibleLedgerList" :key="item.account.id" class="card mx-1">
      <div class="d-flex justify-content-between px-2 pt-1">
        <table class="head_left">
          <tr>
            <td>Account Name</td>
            <td>: <strong>{{ item.account.name }}</strong></td>
          </tr>
          <tr>
            <td>Report Period</td>
            <td>: <strong>{{ start }} to {{ end }}</strong></td>
          </tr>
        </table>

        <table class="head_right">
          <tr>
            <td>Opening Balance :</td>
            <td><strong>{{ commaFormat(item.opening_balance) }}</strong></td>
          </tr>
          <tr>
            <td>Total Debit :</td>
            <td><strong>{{ commaFormat(item.total_debit) }}</strong></td>
          </tr>
          <tr>
            <td>Total Credit :</td>
            <td><strong>{{ commaFormat(item.total_credit) }}</strong></td>
          </tr>
          <tr>
            <td>Closing Balance :</td>
            <td><strong>{{ commaFormat(item.closing_balance) }}</strong></td>
          </tr>
        </table>
      </div>

      <LedgerTable
        :openingBalance="item.opening_balance"
        :closingBalance="item.closing_balance"
        :totalDebitBalance="item.total_debit"
        :totalCreditBalance="item.total_credit"
        :currentPage="item.ledgers.current_page"
        :lastPage="item.ledgers.last_page"
        :ledgers="item.ledgers.data"
      />

      <div class="px-2 pb-2">
        <Paginate
          :page-count="item.ledgers.last_page"
          :force-page="item.ledgers.current_page"
          :click-handler="(page) => changePage(item, page)"
          :prev-text="'Prev'"
          :next-text="'Next'"
        />
      </div>
    </div>

    <div v-if="ledgerList.length > visibleCount" class="text-center py-3">
      <button class="btn btn-outline-primary btn-sm" @click="loadMore">
        Load More ({{ visibleCount }} / {{ ledgerList.length }} shown)
      </button>
    </div>

    <ContactDropdownFilter
      v-if="store.state.isModalOpenThree"
      @onSearchContact="onSearchContact"
      :isFilterable="true"
      :companyRoles="companyRoles"
    />
    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { inject, reactive, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import LedgerTable from '@/components/molecule/company/ledger/LedgerTable'
import Loader from '@/components/atom/LoaderComponent'
import Paginate from 'vuejs-paginate-next'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import handleReport from '@/services/modules/accountingReport'
import figureFormatter from '@/services/utils/figureFormatter'
import handleContact from '@/services/modules/contact'
import handleCBusinesses from '@/services/modules/businesses'
import handleProjects from '@/services/modules/procurement/project'
import handleCostCentres from '@/services/modules/ngo/costCentre'
import handleImportTracker from '@/services/modules/procurement/importTracker'
import handleRole from '@/services/modules/role'
import handleTagRegister from '@/services/modules/scm/tag-register'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'

const showError                  = inject('showError')
const route                      = useRoute()
const store                      = useStore()
const { getGeneralLedgerAll, getLedger, fetchHome, reportLoading } = handleReport()
const { commaFormat }            = figureFormatter()
const { fetchContactProfiles }   = handleContact()
const { formatPatientLabel }     = useAsyncDropdownHelper()
const { fetchBusinessList }      = handleCBusinesses()
const { fetchProjects }          = handleProjects()
const { fetchCostCentreList }    = handleCostCentres()
const { fetchContacts }          = handleImportTracker()
const { fetchCompanyDefaultRoles } = handleRole()
const { fetchTagRegisterList }   = handleTagRegister()

const ledgerList     = ref([])
const loading        = ref(false)
const pageLoading    = reactive({})
const offset         = ref(20)
const visibleCount   = ref(10)

const contactProfile  = ref(null)
const businessId     = ref(null)
const costCenterId  = ref(null)
const projectId      = ref(null)
const voucherType    = ref(null)
const txnType        = ref(null)
const createdBy      = ref(null)
const tagRegisterId = ref(null)

const businesses  = ref([])
const costCentres = ref([])
const projects    = ref([])
const contactList = ref([])
const tagRegisters = ref([])
const companyRoles = ref([])
const homeData    = ref({ txn_type: {}, voucher_type: {} })

const visibleLedgerList = computed(() => ledgerList.value.slice(0, visibleCount.value))

const companyId = computed(() => route.params.companyId)
const start     = computed(() => route.query.start)
const end       = computed(() => route.query.end)
const typeKeys  = computed(() => Object.keys(homeData.value.txn_type))
const token     = computed(() => localStorage.getItem('token'))

function getQuery() {
  let q = '?company_id=' + companyId.value
  if (start.value)          q += '&start_date=' + start.value
  if (end.value)            q += '&end_date=' + end.value
  if (contactProfile.value) q += '&contact_profile_id=' + contactProfile.value.id
  if (businessId.value)    q += '&business_id=' + businessId.value
  if (costCenterId.value) q += '&cost_centre_id=' + costCenterId.value
  if (projectId.value)     q += '&project_id=' + projectId.value
  if (voucherType.value)   q += '&voucher_type=' + voucherType.value
  if (txnType.value)       q += '&txn_type=' + txnType.value
  if (createdBy.value)     q += '&created_by=' + createdBy.value
  if (tagRegisterId.value) q += '&tag_register_id=' + tagRegisterId.value
  return q
}

function loadMore() {
  visibleCount.value += 10
}

function onScroll() {
  if (visibleCount.value >= ledgerList.value.length) return
  const scrolled = window.innerHeight + window.scrollY
  if (scrolled >= document.body.offsetHeight - 300) visibleCount.value += 10
}

async function onClickGo() {
  ledgerList.value = []
  visibleCount.value = 10
  loading.value    = true
  try {
    const res = await getGeneralLedgerAll(getQuery() + '&offset=' + offset.value)
    if (!res.status) {
      showError(res.message);
      return
    }
    ledgerList.value = res.data
  } catch {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

async function changePage(item, page) {
  pageLoading[item.account.id] = true
  try {
    const query = getQuery() + '&page=' + page + '&offset=' + offset.value
    const res   = await getLedger({ headId: item.account.id, query })
    if (!res.status) {
      showError(res.message);
      return
    }
    const { accounts, ...rest } = res.data
    Object.assign(item, { account: accounts, ...rest })
  } catch {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    pageLoading[item.account.id] = false
  }
}

function exportTable() {
  const url = `${process.env.VUE_APP_BASE_URL}/export/reports/general-ledger-all${getQuery()}&_token=${token.value}&is_export=1`
  const a = document.createElement('a')
  a.setAttribute('href', url)
  a.click()
}

function onOpenContactSearchModal() {
  store.state.isModalOpenThree = true
}

function onSearchContact(contact) {
  contactProfile.value = contact
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  const companyQuery = '?company_id=' + companyId.value
  const projectQuery = companyQuery + '&with_donors=1'

  fetchContacts(companyQuery).then(res => { if (res.data) contactList.value = res.data })
  fetchProjects(projectQuery).then(res => { if (res.data) projects.value = res.data })
  fetchCostCentreList(companyQuery).then(res => { if (res.data) costCentres.value = res.data })
  fetchTagRegisterList(companyQuery).then(res => { if (res.data) tagRegisters.value = res.data })
  fetchBusinessList(companyQuery).then(res => { if (res.data) businesses.value = res.data })
  fetchCompanyDefaultRoles(companyQuery).then(res => { if (res.status) companyRoles.value = res.data })
  fetchHome().then(res => { if (res.status) homeData.value = res.data })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<style scoped>
.head_right td { text-align: right; }
.head_left td  { text-align: left; }
</style>
